/**
 * Sets Sentry context (tenantId, userId) and navigation breadcrumbs from router/session.
 * Renders nothing. Mount inside SessionProvider so useSession is available.
 * Tag "tenantId" is set when on a tenant-scoped route (/tenant/[tenantId]/... or /v2/tenant/[tenantId]/...).
 */

import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { useEffect, useRef } from "react";
import useTenantId from "../hooks/useTenantId";
import { setSentryContext, breadcrumbNavigation } from "../lib/sentry";
import { deriveTenantSlugFromHost } from "../lib/sentry/tenantTag";
import { normalizeHost } from "../config/tenants";

export default function SentryContextUpdater() {
  const router = useRouter();
  const tenantId = useTenantId();
  const { data: session, status } = useSession();
  const prevPathRef = useRef<string | null>(null);

  // Set context when session or tenant changes (single canonical source for tenantId tag)
  useEffect(() => {
    const userId = session?.user?.sub ?? undefined;
    // ATT-2872 — populate `domain` + `tenantSlug` from the current hostname.
    // Browser-only (SSR has no `window`); falls back to undefined for the two
    // tags so `setSentryContext` skips them and the dashboard filter shows no
    // value rather than an empty string.
    const domain = typeof window !== "undefined"
      ? normalizeHost(window.location.hostname)
      : undefined;
    const tenantSlug = domain ? deriveTenantSlugFromHost(domain) : undefined;

    setSentryContext({
      tenantId: tenantId ?? null,
      userRole: null,
      userId: userId ?? null,
      domain: domain ?? null,
      tenantSlug: tenantSlug ?? null,
    });
  }, [tenantId, session?.user?.sub, status]);

  // Breadcrumb on route change (client-side only)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = router.asPath ?? "";
    if (path && path !== prevPathRef.current) {
      prevPathRef.current = path;
      breadcrumbNavigation(path, { pathname: router.pathname });
    }
  }, [router.asPath, router.pathname]);

  return null;
}
